/**
 * Session Tracking Hook
 *
 * Automatically tracks student session activity for engagement analysis.
 * Part of Phase 3 of the Lomi Context Framework.
 *
 * Features:
 * - Session lifecycle (start, background, foreground, end)
 * - Screen view tracking
 * - Component view tracking with time spent
 * - Answer submission tracking
 * - Idle detection
 * - Event batching for efficiency
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { sessionAPI, SessionEvent, EventType } from '../services/api';

// Idle timeout in ms (2 minutes)
const IDLE_TIMEOUT = 2 * 60 * 1000;

// Batch flush interval in ms (10 seconds)
const BATCH_FLUSH_INTERVAL = 10 * 1000;

// Max events in batch before auto-flush
const MAX_BATCH_SIZE = 20;

interface UseSessionTrackerOptions {
  enabled?: boolean;
  isAuthenticated?: boolean; // Triggers session start on login
  idleTimeout?: number;
  batchFlushInterval?: number;
}

interface SessionTrackerContext {
  sessionId: string | null;
  isActive: boolean;
  trackScreenView: (screen: string, metadata?: Record<string, any>) => void;
  trackComponentView: (componentId: string, componentType: string, moduleId?: string, order?: number) => void;
  trackComponentExit: (componentId: string, componentType: string, moduleId?: string) => void;
  trackAnswerSubmit: (componentId: string, isCorrect: boolean, metadata?: Record<string, any>) => void;
  trackHelpRequest: (question: string, context?: Record<string, any>) => void;
  trackModuleStart: (moduleId: string) => void;
  trackModuleComplete: (moduleId: string) => void;
  flushEvents: () => Promise<void>;
}

export function useSessionTracker(options: UseSessionTrackerOptions = {}): SessionTrackerContext {
  const {
    enabled = true,
    isAuthenticated = false,
    idleTimeout = IDLE_TIMEOUT,
    batchFlushInterval = BATCH_FLUSH_INTERVAL,
  } = options;

  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(false);

  // Event batch
  const eventBatch = useRef<SessionEvent[]>([]);
  const batchFlushTimer = useRef<NodeJS.Timeout | null>(null);

  // Idle detection
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const isIdle = useRef(false);
  const idleStartTime = useRef<number | null>(null);

  // Component view timing
  const componentViewStart = useRef<Map<string, number>>(new Map());

  // Last activity timestamp
  const lastActivity = useRef<number>(Date.now());

  /**
   * Add event to batch
   */
  const addEvent = useCallback((event: SessionEvent) => {
    if (!enabled) return;

    eventBatch.current.push({
      ...event,
      timestamp: new Date().toISOString(),
    });

    // Auto-flush if batch is full
    if (eventBatch.current.length >= MAX_BATCH_SIZE) {
      flushEvents();
    }
  }, [enabled]);

  /**
   * Flush event batch to server
   */
  const flushEvents = useCallback(async () => {
    if (eventBatch.current.length === 0) return;

    const events = [...eventBatch.current];
    eventBatch.current = [];

    try {
      await sessionAPI.recordEvents(events);
    } catch (error) {
      // Put events back in batch on failure
      eventBatch.current = [...events, ...eventBatch.current];
      console.warn('Failed to flush session events:', error);
    }
  }, []);

  /**
   * Reset idle timer on user activity
   */
  const resetIdleTimer = useCallback(() => {
    lastActivity.current = Date.now();

    // If was idle, record idle_end
    if (isIdle.current && idleStartTime.current) {
      const idleDuration = Date.now() - idleStartTime.current;
      addEvent({
        event_type: 'idle_end',
        metadata: { idle_duration_ms: idleDuration },
      });
      isIdle.current = false;
      idleStartTime.current = null;
    }

    // Clear and restart idle timer
    if (idleTimer.current) {
      clearTimeout(idleTimer.current);
    }

    idleTimer.current = setTimeout(() => {
      if (!isIdle.current) {
        isIdle.current = true;
        idleStartTime.current = Date.now();
        addEvent({
          event_type: 'idle_start',
          screen: location.pathname,
        });
      }
    }, idleTimeout);
  }, [idleTimeout, location.pathname, addEvent]);

  /**
   * Track screen view
   */
  const trackScreenView = useCallback((screen: string, metadata?: Record<string, any>) => {
    addEvent({
      event_type: 'screen_view',
      screen,
      metadata,
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  /**
   * Track component view (start timing)
   */
  const trackComponentView = useCallback((
    componentId: string,
    componentType: string,
    moduleId?: string,
    order?: number
  ) => {
    componentViewStart.current.set(componentId, Date.now());
    addEvent({
      event_type: 'component_view',
      component_id: componentId,
      component_type: componentType,
      module_id: moduleId,
      component_order: order,
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  /**
   * Track component exit (with time spent)
   */
  const trackComponentExit = useCallback((
    componentId: string,
    componentType: string,
    moduleId?: string
  ) => {
    const startTime = componentViewStart.current.get(componentId);
    const duration = startTime ? Date.now() - startTime : undefined;
    componentViewStart.current.delete(componentId);

    addEvent({
      event_type: 'component_exit',
      component_id: componentId,
      component_type: componentType,
      module_id: moduleId,
      metadata: { visible_duration_ms: duration },
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  /**
   * Track answer submission
   */
  const trackAnswerSubmit = useCallback((
    componentId: string,
    isCorrect: boolean,
    metadata?: Record<string, any>
  ) => {
    const startTime = componentViewStart.current.get(componentId);
    const timeToAnswer = startTime ? Date.now() - startTime : undefined;

    addEvent({
      event_type: 'answer_submit',
      component_id: componentId,
      metadata: {
        is_correct: isCorrect,
        time_to_answer_ms: timeToAnswer,
        ...metadata,
      },
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  /**
   * Track help request (Ask Lomi)
   */
  const trackHelpRequest = useCallback((question: string, context?: Record<string, any>) => {
    addEvent({
      event_type: 'help_request',
      screen: location.pathname,
      metadata: {
        question_text: question.substring(0, 200), // Truncate for storage
        ...context,
      },
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer, location.pathname]);

  /**
   * Track module start
   */
  const trackModuleStart = useCallback((moduleId: string) => {
    addEvent({
      event_type: 'module_start',
      module_id: moduleId,
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  /**
   * Track module complete
   */
  const trackModuleComplete = useCallback((moduleId: string) => {
    addEvent({
      event_type: 'module_complete',
      module_id: moduleId,
    });
    resetIdleTimer();
  }, [addEvent, resetIdleTimer]);

  // Start session when user becomes authenticated
  useEffect(() => {
    if (!enabled) return;
    if (!isAuthenticated) {
      // User logged out - end session
      if (sessionId) {
        // Flush remaining events before ending session
        if (eventBatch.current.length > 0) {
          sessionAPI.recordEvents(eventBatch.current).catch(() => {});
          eventBatch.current = [];
        }
        sessionAPI.endSession().catch(() => {});
        setSessionId(null);
        setIsActive(false);
        console.debug('[SessionTracker] Session ended on logout');
      }
      return;
    }

    // User is authenticated - start session if not already active
    if (sessionId) return; // Already have a session

    const startSession = async () => {
      try {
        const result = await sessionAPI.startSession();
        setSessionId(result.session_id);
        setIsActive(true);
        console.debug('[SessionTracker] Session started:', result.session_id);
      } catch (error) {
        console.warn('[SessionTracker] Failed to start session:', error);
      }
    };

    startSession();

    // Cleanup on unmount
    return () => {
      if (eventBatch.current.length > 0) {
        // Sync flush on unmount (best effort)
        sessionAPI.recordEvents(eventBatch.current).catch(() => {});
      }
    };
  }, [enabled, isAuthenticated, sessionId]);

  // Track screen changes
  useEffect(() => {
    if (!enabled || !isActive) return;
    trackScreenView(location.pathname);
  }, [location.pathname, enabled, isActive, trackScreenView]);

  // Setup batch flush interval
  useEffect(() => {
    if (!enabled || !isActive) return;

    batchFlushTimer.current = setInterval(() => {
      flushEvents();
    }, batchFlushInterval);

    return () => {
      if (batchFlushTimer.current) {
        clearInterval(batchFlushTimer.current);
      }
    };
  }, [enabled, isActive, batchFlushInterval, flushEvents]);

  // Setup idle detection event listeners
  useEffect(() => {
    if (!enabled) return;

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer, { passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimer.current) {
        clearTimeout(idleTimer.current);
      }
    };
  }, [enabled, resetIdleTimer]);

  // Handle visibility change (tab switch, minimize)
  useEffect(() => {
    if (!enabled || !isActive) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App going to background
        addEvent({
          event_type: 'app_background',
          screen: location.pathname,
        });
        flushEvents(); // Flush before backgrounding
      } else {
        // App coming to foreground
        addEvent({
          event_type: 'app_foreground',
          screen: location.pathname,
        });
        resetIdleTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isActive, location.pathname, addEvent, flushEvents, resetIdleTimer]);

  // Handle page unload
  useEffect(() => {
    if (!enabled || !isActive) return;

    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable delivery on page close
      const events = eventBatch.current;
      if (events.length > 0) {
        const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' });
        navigator.sendBeacon('/api/session/events', blob);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, isActive]);

  // Listen for external flush requests (e.g., from E2E tests)
  useEffect(() => {
    if (!enabled) return;

    const handleFlushRequest = () => {
      console.debug('[SessionTracker] External flush requested');
      flushEvents();
    };

    window.addEventListener('flush-session-events', handleFlushRequest);

    return () => {
      window.removeEventListener('flush-session-events', handleFlushRequest);
    };
  }, [enabled, flushEvents]);

  return {
    sessionId,
    isActive,
    trackScreenView,
    trackComponentView,
    trackComponentExit,
    trackAnswerSubmit,
    trackHelpRequest,
    trackModuleStart,
    trackModuleComplete,
    flushEvents,
  };
}

export default useSessionTracker;
